import React, { useState, useEffect } from "react";
import axios from "axios";
import API_BASE_URL from "../../config";
import '../Styles/Profile.css';
import { FaEdit, FaGithub, FaLinkedin, FaEnvelope, FaIdCard, FaCamera, FaPhone, FaUser, FaImage } from "react-icons/fa";
import { FiLogOut, FiSave, FiX } from "react-icons/fi";
import NavBar from "../NavBar";

export default function ProfileDashboard() {
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);

  // Editable fields
  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const [bio, setBio] = useState("");
  const [github, setGithub] = useState("");
  const [linkedin, setLinkedin] = useState("");
  const [profileImage, setProfileImage] = useState(null);
  const [previewImage, setPreviewImage] = useState("");

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/api/auth/profile`, {
          withCredentials: true,
        });
        setProfileData(res.data);
        setFullName(res.data.fullName || "");
        setPhone(res.data.phone || "");
        setBio(res.data.bio || "");
        setGithub(res.data.github || "");
        setLinkedin(res.data.linkedin || "");
        setPreviewImage(
          res.data.profileImage
            ? `${API_BASE_URL}${res.data.profileImage}?t=${Date.now()}`
            : ""
        );
      } catch (error) {
        console.error("Error fetching profile:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, []);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select a valid image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Image size should be less than 5MB');
      return;
    }

    setProfileImage(file);
    setPreviewImage(URL.createObjectURL(file));
  };

  const handleSave = async () => {
    setSaving(true);
    const formData = new FormData();
    formData.append("fullName", fullName);
    formData.append("phone", phone);
    formData.append("bio", bio);
    formData.append("github", github);
    formData.append("linkedin", linkedin);
    if (profileImage) formData.append("profileImage", profileImage);

    try {
      const res = await axios.put(
        `${API_BASE_URL}/api/auth/profile/update`,
        formData,
        {
          withCredentials: true,
          headers: { "Content-Type": "multipart/form-data" },
        }
      );
      setProfileData(res.data);
      setEditMode(false);

      // Update previewImage from updated data
      if (res.data.profileImage) {
        const newImageUrl = `${API_BASE_URL}${res.data.profileImage}?t=${Date.now()}`;
        setPreviewImage(newImageUrl);
        setProfileImage(null);
      }

      alert("Profile updated successfully!");
    } catch (err) {
      console.error("Error updating profile:", err);
      const message = err.response?.data?.message || "Failed to update profile.";
      alert(`Error: ${message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    // Reset to original values
    setFullName(profileData.fullName || "");
    setPhone(profileData.phone || "");
    setBio(profileData.bio || "");
    setGithub(profileData.github || "");
    setLinkedin(profileData.linkedin || "");
    setPreviewImage(
      profileData.profileImage
        ? `${API_BASE_URL}${profileData.profileImage}?t=${Date.now()}`
        : ""
    );
    setProfileImage(null);
    setEditMode(false);
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API_BASE_URL}/api/auth/logout`, {}, { withCredentials: true });
      localStorage.removeItem("isLoggedIn");
      window.location.href = "/";
    } catch (err) {
      console.error("Logout error", err);
    }
  };

  if (loading) {
    return (
      <div className="profile-page-wrapper">
        <NavBar />
        <div className="loading-screen">
          <div className="loader"></div>
          <p>Loading Profile...</p>
        </div>
      </div>
    );
  }

  if (!profileData) {
    return (
      <div className="profile-page-wrapper">
        <NavBar />
        <div className="error-screen">
          <p>Please Log In to view Profile</p>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-page-wrapper">
      <NavBar />

      <div className="profile-main-container">
        <div className="profile-card">
          {/* Header Section */}
          <div className="profile-header">
            <div className="header-content">
              <h1>My Profile</h1>
              <p className="header-subtitle">Manage your account information</p>
            </div>
            <button className="logout-btn" onClick={handleLogout} title="Logout">
              <FiLogOut /> Logout
            </button>
          </div>

          <div className="profile-content">
            {/* Left Side: Avatar & Quick Info */}
            <div className="profile-avatar-section">
              <div className="avatar-container">
                <div className="avatar-wrapper">
                  {previewImage ? (
                    <img
                      src={previewImage}
                      alt="Profile"
                      className="avatar-img"
                    />
                  ) : (
                    <div className="avatar-placeholder">
                      <FaImage className="placeholder-icon" />
                    </div>
                  )}
                  {editMode && (
                    <label htmlFor="fileInput" className="camera-overlay">
                      <FaCamera />
                      <span>{previewImage ? "Change Photo" : "Upload Photo"}</span>
                    </label>
                  )}
                  <input
                    type="file"
                    id="fileInput"
                    hidden
                    accept="image/*"
                    onChange={handleImageChange}
                    disabled={!editMode}
                  />
                </div>
              </div>

              <div className="user-info-quick">
                <h2 className="user-fullname">{fullName || profileData.fullName}</h2>
                <p className="user-role">
                  {profileData.role === 'admin' ? '👑 Administrator' : '👤 Member'}
                </p>
              </div>

              <div className="quick-stats">
                <div className="stat-item">
                  <span className="stat-label">Member Since</span>
                  <span className="stat-value">2024</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Status</span>
                  <span className="stat-value status-active">Active</span>
                </div>
              </div>
            </div>

            {/* Right Side: Detailed Information */}
            <div className="profile-details-section">
              <div className="section-header">
                <h3>Personal Information</h3>
                {!editMode ? (
                  <button className="edit-btn" onClick={() => setEditMode(true)}>
                    <FaEdit /> Edit Profile
                  </button>
                ) : (
                  <div className="action-buttons">
                    <button
                      className="save-btn"
                      onClick={handleSave}
                      disabled={saving}
                    >
                      <FiSave /> {saving ? 'Saving...' : 'Save'}
                    </button>
                    <button
                      className="cancel-btn"
                      onClick={handleCancel}
                      disabled={saving}
                    >
                      <FiX /> Cancel
                    </button>
                  </div>
                )}
              </div>

              <div className="info-grid">
                {/* Full Name */}
                <div className="info-item">
                  <label><FaUser /> Full Name</label>
                  {editMode ? (
                    <input
                      type="text"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      placeholder="Enter your full name"
                      className="input-field"
                    />
                  ) : (
                    <p>{profileData.fullName}</p>
                  )}
                </div>

                {/* Email (Read-only) */}
                <div className="info-item">
                  <label><FaEnvelope /> Email</label>
                  <p className="readonly-field">{profileData.email}</p>
                </div>

                {/* University ID (Read-only) */}
                <div className="info-item">
                  <label><FaIdCard /> University ID</label>
                  <p className="readonly-field">{profileData.universityId}</p>
                </div>

                {/* Phone */}
                <div className="info-item">
                  <label><FaPhone /> Phone Number</label>
                  {editMode ? (
                    <input
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      placeholder="+1 (555) 000-0000"
                      className="input-field"
                    />
                  ) : (
                    <p>{profileData.phone || <span className="placeholder-text">Not provided</span>}</p>
                  )}
                </div>

                {/* Bio */}
                <div className="info-item full-width">
                  <label>📝 Bio / About</label>
                  {editMode ? (
                    <textarea
                      value={bio}
                      onChange={(e) => setBio(e.target.value)}
                      placeholder="Tell us about yourself..."
                      className="input-field textarea-field"
                      rows="4"
                    />
                  ) : (
                    <p className="bio-text">
                      {profileData.bio || <span className="placeholder-text">No bio added yet</span>}
                    </p>
                  )}
                </div>

                {/* GitHub */}
                <div className="info-item full-width">
                  <label><FaGithub /> GitHub Profile</label>
                  {editMode ? (
                    <input
                      type="url"
                      value={github}
                      onChange={(e) => setGithub(e.target.value)}
                      placeholder="https://github.com/username"
                      className="input-field"
                    />
                  ) : (
                    <p>
                      {profileData.github ? (
                        <a href={profileData.github} target="_blank" rel="noopener noreferrer">
                          {profileData.github}
                        </a>
                      ) : (
                        <span className="placeholder-text">Not provided</span>
                      )}
                    </p>
                  )}
                </div>

                {/* LinkedIn */}
                <div className="info-item full-width">
                  <label><FaLinkedin /> LinkedIn Profile</label>
                  {editMode ? (
                    <input
                      type="url"
                      value={linkedin}
                      onChange={(e) => setLinkedin(e.target.value)}
                      placeholder="https://linkedin.com/in/username"
                      className="input-field"
                    />
                  ) : (
                    <p>
                      {profileData.linkedin ? (
                        <a href={profileData.linkedin} target="_blank" rel="noopener noreferrer">
                          {profileData.linkedin}
                        </a>
                      ) : (
                        <span className="placeholder-text">Not provided</span>
                      )}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>


    </div>
  );
}
